import { get, post } from '../helpers/api.helper';
import config from '../config/config';
import { ServiceResponseType, parseServiceResponse } from '../helpers/serviceParsing';

const { matching_api } = config.api;

// 구독 정보 타입 정의
export interface SubscriptionFeature {
    text: string;
    isIncluded: boolean;
}

export interface SubscriptionProduct {
    id: string;
    name: string;
    creditAmount: number;
    originalPrice: number;
    discountAmount: number;
    totalCharge: number;
    description: string;
    features: SubscriptionFeature[];
    billingCycle?: string; // 'monthly' | 'yearly'
    createdAt: string;
    updatedAt: string;
}

export interface UserSubscription {
    id: string;
    bid: string;
    startDate: string;
    endDate: string;
    status: string;
    billingType: 'online' | 'cms';
    lastBilledAt: string;
    nextBillingAt: string;
    canceledBillingAt: string | null;
    updatedAt: string;
    createdAt: string;
    product: SubscriptionProduct;
}

export interface UserSubscriptionResponse {
    item: UserSubscription;
}

// 카드 정보 타입 정의
export interface UserCard {
    id: string;
    bid: string;
    cardNo: string;
    cardCode: string;
    cardName: string;
    isUsed: string;
    deletedAt: string | null;
    updatedAt: string;
    createdAt: string;
}

export interface UserCardResponse {
    itemList: UserCard[];
}

// 주문 내역 타입 정의
export interface OrderPayment {
    id: string;
    transactionId: string;
    bid: string;
    paidAmount: number;
    refundedAmount: number;
    paymentMethod: string;
    installmentMonths: number;
    status: string;
    receiptUrl: string | null;
    meta: any;
    transactionedAt: string;
    createdAt: string;
}

export interface OrderItem {
    id: string;
    orderNumber: string;
    productName: string;
    orderStatus: string;
    orderStatusCode: number;
    updatedAt: string;
    createdAt: string;
    product: SubscriptionProduct;
    fittingOrderPayment: OrderPayment;
}

export interface OrderResponse {
    itemList: OrderItem[];
}

// 크레딧 상품 타입 정의
export interface CreditProduct {
    id: string;
    name: string;
    creditAmount: number;
    originalPrice: number;
    discountAmount: number;
    totalCharge: number;
    description: string;
    features: SubscriptionFeature[] | null;
    createdAt: string;
    updatedAt: string;
    fittingSubscriptionPlans: any[];
}

export interface CreditProductResponse {
    data: CreditProduct[];
    count: number;
    total: number;
    page: number;
    pageCount: number;
}

/**
 * 사용자의 구독 정보를 가져옵니다.
 * @param userId 사용자 ID
 * @returns 사용자의 구독 정보
 */
export async function getUserSubscription(userId: string): Promise<UserSubscriptionResponse> {
    try {
        const response = await get(`${matching_api}/billing/user/${userId}/subscription`);
        return parseServiceResponse<UserSubscriptionResponse>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

/**
 * 사용자의 등록된 카드 정보를 가져옵니다.
 * @param userId 사용자 ID
 * @returns 사용자의 카드 정보 목록
 */
export async function getUserCards(userId: string): Promise<UserCardResponse> {
    try {
        const response = await get(`${matching_api}/billing/user/${userId}/card`);
        // parseServiceResponse를 사용하지 않고 직접 처리
        return {
            itemList: response?.data?.itemList || [],
            status: response?.status || -1,
        } as UserCardResponse;
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

/**
 * 사용자의 주문 내역을 가져옵니다.
 * @param userId 사용자 ID
 * @returns 사용자의 주문 내역 목록
 */
export async function getUserOrders(userId: string): Promise<OrderResponse> {
    try {
        const response = await get(`${matching_api}/billing/user/${userId}/order`);
        return parseServiceResponse<OrderResponse>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

/**
 * 크레딧 상품 목록을 가져옵니다. (fittingSubscriptionPlans가 비어있는 상품만)
 * @returns 크레딧 상품 목록
 */
export async function getCreditProducts(): Promise<CreditProductResponse> {
    try {
        const response = await get(`${matching_api}/fitting/product`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * 크레딧 상품을 구매합니다.
 * @param userId 사용자 ID
 * @param productId 상품 ID
 * @returns 구매 결과
 */
export async function purchaseCreditProduct(userId: string, productId: string): Promise<any> {
    try {
        const response = await post(`${matching_api}/billing/user/${userId}/product/${productId}`);
        return response;
    } catch (error) {
        throw error;
    }
}

/**
 * 주문을 취소합니다.
 * @param userId 사용자 ID
 * @param orderId 주문 ID
 * @returns 취소 결과
 */
export async function cancelOrder(userId: string, orderId: string): Promise<any> {
    try {
        const response = await post(`${matching_api}/billing/user/${userId}/order/${orderId}/cancel`);
        return response;
    } catch (error) {
        throw error;
    }
}

/**
 * 주문을 환불합니다.
 * @param userId 사용자 ID
 * @param orderId 주문 ID
 * @returns 환불 결과
 */
export async function refundOrder(userId: string, orderId: string): Promise<any> {
    try {
        const response = await post(`${matching_api}/billing/user/${userId}/order/${orderId}/refund`);
        return response;
    } catch (error) {
        throw error;
    }
}
